import * as constants from './utils/constants.js';
import { userCurrency } from './utils/user.currency.js';
import { userLocale } from './utils/user.locale.js';
import { momentUtil } from './utils/moment.timezone.js';
const currencyDetails = userCurrency.currencyDetails;
const localeDetails = userLocale.localeDetails;
const currentUTCDate = new Date().toISOString();

/**
 * Summary:  This function is to format the date fields based on the locale, timezone and date.
 * @since      1.5.0
 * @access     public
 * @param {type}   const           date : The date should be a date and time parts. 
 * @param {type}   const           locale : locale should be a string otherwise a default locale will be returned.
 * @param {type}   const           timeZone : timeZone should be a string otherwise a default timeZone will be returned.
 * @param {type}   const           withTime : withTime should be a boolean value otherwise the data will be returned with time
 * @return {String} Returns string which displays the time format based on locale and timeZone.
 */

const _formatDateWithoutTime = (date, format = constants.DEFAULT_DATE_FORMAT) => {
    return momentUtil.formatDateOnly(date, format);
}
const _convertDateDataType = (date, locale = constants.DEFAULT_LOCALE, timeZone = constants.DEFAULT_TIMEZONE, withTime = true) => {
    if (isNaN(Date.parse(date))) {
        return constants.NA;
    } else {
        const localeData = momentUtil.localeData(locale);
        let dateTimeFormat = localeData.longDateFormat("L")
        withTime && (dateTimeFormat = dateTimeFormat + ', ' + localeData.longDateFormat("LTS"))
        const dateObj = momentUtil.timezoneConvertor(date, timeZone);
        return momentUtil.formatDateTime(dateObj, dateTimeFormat);
    }
}


/**
 * Summary:  This function is to format the timezone based on the locale and timezone.
 * @since      1.5.0
 * @access     public
 * @param {type}   const           locale : locale should be a string otherwise a default locale will be returned.
 * @param {type}   const           timezone : timezone should be a string otherwise a default timeZone will be returned.
 * @param {type}   const           date : date should be a string otherwise a default current UTC date will be returned.
 * @return {String} Returns string which displays the time format based on locale and timeZone.
 */
const _convertToTimezone = (locale = constants.DEFAULT_LOCALE, timezone = constants.DEFAULT_TIMEZONE, date = currentUTCDate) => {
    const localeData = momentUtil.localeData(locale);
    const momentObj = momentUtil.dateTimeConvertor(timezone, date);
    const dateObj = momentUtil.timezoneConvertor(date, timezone);
    return {
        isoString: momentObj.format(),
        utcString: momentObj.toString(),
        getTime: () => momentUtil.formatDateTime(dateObj, localeData.longDateFormat("LTS")),
        getDate: () => momentUtil.formatDateTime(dateObj, localeData.longDateFormat("L")),
        meridian: momentObj.format('A')
    }
}

/*
* This function is to format Percentage fields 
* convertPercentDataType(12) = 12%
*/
const _convertPercentDataType = (data) => {
    return isNaN(parseFloat(data)) ? data : data + constants.PERCENTAGE;
}


/*
* This function is to format month fields 
* convertMonthsDataType(12) = 12 Months
*/
const _convertMonthsDataType = (data) => {
    return isNaN(parseFloat(data)) ? data : data + constants.MONTHS;
}

/**
 * Summary: This function is to get the locale and return the locale details
 * @since      1.5.0
 * @access     public
 * @param {type}   const           locale : locale should be a string otherwise a default locale will be returned.
 * @return {object} Returns object which consists regex, isPrefix, delimiter, thousandGroup.
 */
const _getLocaleData = (locale = constants.DEFAULT_LOCALE) => {
    return localeDetails[locale.toLowerCase()] || localeDetails[constants.DEFAULT_LOCALE.toLowerCase()];
};

/**
 * Summary: This function is to get the currency and return the currency symbol
 * @since      1.5.0
 * @access     public
 * @param {type}   const           currency : currency should be a string/Special Character otherwise a default currency symbol will be returned.
 * @return {value} Returns value which consists currency symbol.
 */
const _getCurrencySymbol = (currency = constants.DEFAULT_CURRENCY) => {
    return currencyDetails[currency.toLowerCase()] || currency;
};

/**
 * Summary: This function is to format currency fields 
 * @since      1.5.0
 * @access     public
 * @param {type}   const           data : The data should be a Integer or Decimal value 
 * @param {type}   const           locale : locale should be a string otherwise a default locale will be returned.
 * @param {type}   const           currency : currency : currency should be a string/Special Character otherwise a default currency symbol will be returned.
 * @return {String} Returns string which consists currency symbol and the currency value(data).
 */
const _convertCurrencyDataType = (data, locale = constants.DEFAULT_LOCALE, currency = constants.DEFAULT_CURRENCY) => {
    const convertedValue = _convertGeneralDataType(data, locale);
    const currencySymbol = _getCurrencySymbol(currency);
    const isPrefix = _getLocaleData(locale).isPrefix;
    // If negative, place currency symbol inside parentheses
    if (convertedValue.startsWith('(') && convertedValue.endsWith(')')) {
        const innerValue = convertedValue.slice(1, -1);
        return isPrefix
            ? `(${currencySymbol}${innerValue})`
            : `(${innerValue} ${currencySymbol})`;
    }
    return isPrefix ? `${currencySymbol}${convertedValue}` : `${convertedValue} ${currencySymbol}`;
}

/**
 * Summary:  This function is to format general dataType for the fields coming from spreading.
 * @since      1.5.0
 * @access     public
 * @param {type}   const           data : The data should be a Integer or Decimal value 
 * @param {type}   const           locale : locale should be a string otherwise a default locale will be returned.
 * @return {String} Returns string which consists the currency value (data) based on currency format.
 */
const _convertGeneralDataType = (data, locale = constants.DEFAULT_LOCALE) => {
    const { regex, delimiter, decimalPoint } = _getLocaleData(locale);
    
    // Keep as string to avoid Number conversion
    const isNegative = String(data).startsWith('-');
    const absValue = String(data).replace('-', '').trim();
    
    // Check for empty or invalid values
    if (!absValue || isNaN(parseFloat(absValue))) {
        return "0.00";
    }
    
    // Ensure 2 decimal places
    const parts = absValue.split('.');
    const intPart = parts[0] || '0';
    const decPart = (parts[1] || '').padEnd(2, '0').slice(0, 2);
    let rounded = `${intPart}.${decPart}`;
    
    decimalPoint && (rounded = rounded.replace('.', decimalPoint));
    const result = rounded.toString().replace(regex, delimiter);
    return isNegative ? `(${result})` : result;
};


/**
 * Summary:  This function is to format the Number based on the country.
 * @since      1.5.0
 * @access     public
 * @param {type}   const           data : The data should be a Integer or Decimal value 
 * @param {type}   const           locale : locale should be a string otherwise a default locale will be returned.
 * @return {String} Returns string which consists the number (data) based on country format(locale).
 */
const _convertNumberFormat = (data, locale = constants.DEFAULT_LOCALE) => {
    const { regex, delimiter } = _getLocaleData(locale);
    let rounded = Number(data) ? data : "0";
    return rounded.toString().replace(regex, delimiter);
}

/*
* To covert a single data field when given with its field data
*/
const _convertFieldData = (fieldObj, dataObj, locale = constants.DEFAULT_LOCALE, currency = constants.DEFAULT_CURRENCY, timeZone = constants.DEFAULT_TIMEZONE) => {
    const value = dataObj && dataObj[(fieldObj || {}).fieldValue];
    switch (((fieldObj || {}).format || "").toLowerCase().trim()) {
        case "date":
            return _convertDateDataType(value, locale, timeZone);
        case "percentage":
            return _convertPercentDataType(value);
        case "months":
            return _convertMonthsDataType(value);
        case "currency":
            return _convertCurrencyDataType(value, locale, currency);
        case "general":
            return _convertGeneralDataType(value, locale);
        default:
            return value;
    }
}

/*
* To covert a full data object when given with fields data
*/
const _formatDataFields = (allFields, dataObj, locale = constants.DEFAULT_LOCALE, currency = constants.DEFAULT_CURRENCY, timeZone = constants.DEFAULT_TIMEZONE) => {
    let formattedObj = {};
    Object.keys(dataObj || {}).forEach(key => {
        let fieldObj = (allFields || []).find(eachField => eachField.fieldValue === key) || {};
        formattedObj[key] = _convertFieldData(fieldObj, dataObj, locale, currency, timeZone) || dataObj[key];
    })
    return formattedObj;
}

/**
 * Summary:  This function is to format date for displaying on screen in the following granularity.
 * seconds -> minutes -> hours -> days -> if days > 7 the actual date
 * @since      1.5.7
 * @access     public
 * @param {type}   const           date : The date (in UTC Format) to be converted
 * @return {String}                Returns string which consists the formated date.
 */
const _dateToDuration = (date, locale = constants.DEFAULT_LOCALE, timeZone = constants.DEFAULT_TIMEZONE) => {

    const currentDate = new Date();
    const previousDate = new Date(date);

    const difference = currentDate.getTime() - previousDate.getTime();

    const differenceInDays = Math.floor(difference / 86400000); // days

    if(differenceInDays > 7) {
        return _convertDateDataType(date, locale, timeZone, false);
    }

    if(differenceInDays > 0) {
        return `${differenceInDays} day${differenceInDays != 1 ? 's' : ''} ago`
    }

    const differenceInHours = Math.floor((difference % 86400000) / 3600000); // hours
    if(differenceInHours > 0) {
        return `${differenceInHours} hour${differenceInHours != 1 ? 's' : ''} ago`
    }

    const differenceInMinutes = Math.round(((difference % 86400000) % 3600000) / 60000); // mins
    if(differenceInMinutes > 0) {
        return `${differenceInMinutes} minute${differenceInMinutes != 1 ? 's' : ''} ago`
    }

    const differenceInSeconds = Math.round((((difference % 86400000) % 3600000) % 60000) / 1000); // seconds
    return `${differenceInSeconds} second${differenceInSeconds != 1 ? 's' : ''} ago`
};

export const formatFileds = {
    convertDateDataType : _convertDateDataType,
    convertFieldData : _convertFieldData,
    convertNumberFormat : _convertNumberFormat,
    convertGeneralDataType : _convertGeneralDataType,
    convertCurrencyDataType : _convertCurrencyDataType,
    getCurrencySymbol : _getCurrencySymbol,
    convertToTimezone : _convertToTimezone,
    getLocaleData : _getLocaleData,
    convertPercentDataType : _convertPercentDataType,
    convertMonthsDataType : _convertMonthsDataType,
    formatDataFields : _formatDataFields,
    dateToDuration : _dateToDuration,
    formatDateWithoutTime : _formatDateWithoutTime
}